/**
 * Δ Anual MtM (energia) do Book Brasil — alimenta a linha "Δ Anual" do card
 * Resultado (delta vs foto 31/12). Lê GET /api/pnl/delta-anual (read-only);
 * cache module-level + startVisiblePolling, MESMO padrão/refetch dos outros
 * hooks do card (#75/#251) — sem polling agressivo. Erro → null (a linha cai
 * no estado "aguardando baseline" honesto).
 */
import { useEffect, useState } from "react";
import { getPnlDeltaAnual } from "../api/client";
import type { DeltaAnualResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 10 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<DeltaAnualResponse>;
}

let cache: CacheEntry | null = null;

export function invalidatePnlDeltaAnualCache(): void {
  cache = null;
}

export interface UsePnlDeltaAnualState {
  data: DeltaAnualResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function usePnlDeltaAnual(): UsePnlDeltaAnualState {
  const [state, setState] = useState<UsePnlDeltaAnualState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    setState((s) => ({ ...s, isLoading: true }));

    function load() {
      const now = Date.now();
      if (!cache || cache.expiresAt < now) {
        cache = { expiresAt: now + REFETCH_MS, promise: getPnlDeltaAnual() };
      }
      cache.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache = null;
          setState({ data: null, isLoading: false, error: err });
        });
    }

    load();
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, []);

  return state;
}
